// Time-based + market×time prediction landing pages.
//
// These drive high-intent SEO surfaces like "bonus picks today", "correct score
// prediction today" and "predictions tomorrow". Each entry reuses the live
// approved-prediction feed, filtered by a kickoff-time window (and optionally a
// market matcher), so the pages always show fresh, actionable picks.

export type Timeframe = "today" | "tomorrow" | "weekend" | "yesterday" | "tonight" | "week";

export interface LandingFaq {
  question: string;
  answer: string;
}

/** Editorial prose block rendered under the prediction grid (H2 + paragraphs). */
export interface LandingSection {
  heading: string;
  /** Optional H3 sub-blocks. */
  paragraphs: string[];
  bullets?: string[];
}

export interface PredictionLanding {
  slug: string;
  label: string;
  h1: string;
  title: string;
  description: string;
  /** Short, unique social headline (og:title / twitter:title). */
  ogTitle?: string;
  /** Short, unique social summary (og:description / twitter:description). */
  ogDescription?: string;
  intro: string;
  keywords: string;
  timeframe: Timeframe;
  /** Optional ilike matchers against the `market_type` column. */
  marketMatchers?: string[];
  /** Optional ilike matchers against the `league` column. */
  leagueMatchers?: string[];
  /** Minimum confidence filter (used by "sure win" surfaces). */
  minConfidence?: number;
  sort?: "confidence" | "soonest" | "odds_desc";
  faqs: LandingFaq[];
  /** In-depth editorial content (avoids thin-content pages). */
  sections?: LandingSection[];
}



export const PREDICTION_LANDINGS: PredictionLanding[] = [
  {
    slug: "tomorrow",
    label: "Predictions Tomorrow",
    h1: "Football Predictions Tomorrow",
    title: "Football Predictions Tomorrow | Tomorrow's Betting Tips",
    description:
      "Tomorrow's football predictions and betting tips with confidence scores, value odds and clear reasoning across every major league and market.",
    intro:
      "Football predictions for tomorrow's fixtures, ranked by confidence with transparent, data-driven reasoning across 1X2, Over/Under, BTTS and correct score markets.",
    keywords:
      "football predictions tomorrow, soccer predictions tomorrow, betting tips tomorrow, tomorrow's tips, correct score tomorrow",
    timeframe: "tomorrow",
    sort: "confidence",
    faqs: [
      {
        question: "How accurate are tomorrow's football predictions?",
        answer:
          "Each pick carries a confidence score from our 50-strategy engine. No prediction is guaranteed, but higher-confidence selections have historically landed more often. Always gamble responsibly.",
      },
      {
        question: "When are tomorrow's predictions updated?",
        answer:
          "Predictions refresh continuously as new odds and team news arrive, so check back closer to kick-off for the sharpest picks.",
      },
    ],
  },
  {
    slug: "weekend",
    label: "Weekend Predictions",
    h1: "Weekend Football Predictions",
    title: "Weekend Football Predictions & Betting Tips | Statlign",
    description:
      "This weekend's football predictions and betting tips across the Premier League, La Liga, Serie A and more, with confidence scores and value odds.",
    intro:
      "Weekend football predictions across every major league, ranked by confidence with clear, data-driven reasoning for Saturday and Sunday fixtures.",
    keywords:
      "weekend football predictions, saturday football tips, sunday predictions, premier league predictions this weekend",
    timeframe: "weekend",
    sort: "confidence",
    faqs: [
      {
        question: "Which leagues do weekend predictions cover?",
        answer:
          "Weekend picks span the Premier League, La Liga, Serie A, Bundesliga, Ligue 1 and other major competitions with fixtures on Saturday and Sunday.",
      },
    ],
  },
  {
    slug: "bonus-picks-today",
    label: "Bonus Picks Today",
    h1: "Bonus Picks: Today's Highest-Confidence Predictions",
    title: "Bonus Picks Today | High-Confidence Football Tips",
    description:
      "Today's highest-confidence football predictions — our strongest bonus picks, each with a confidence score, value odds and clear reasoning. No pick is guaranteed.",
    intro:
      "Our highest-confidence football predictions kicking off today. No bet is ever guaranteed — sport is unpredictable — but these are the bonus picks our engine backs most strongly.",
    keywords:
      "bonus picks today, high confidence football predictions, best football tips today, value odds today, today match predictions",
    timeframe: "today",
    minConfidence: 70,
    sort: "confidence",
    faqs: [
      {
        question: "Are these bonus picks guaranteed to win?",
        answer:
          "No. There is no such thing as a guaranteed or 100% sure football prediction. Football is unpredictable; we surface the highest-confidence, best-value picks and show the reasoning behind each one. Always stake responsibly.",
      },
      {
        question: "What makes a pick a bonus pick here?",
        answer:
          "These are our top-confidence selections for today (70%+ model confidence). They carry the strongest strategy confirmation, but you should still stake responsibly.",
      },
    ],
  },
  {
    slug: "correct-score-today",
    label: "Correct Score Today",
    h1: "Correct Score Predictions Today",
    title: "Correct Score Predictions Today | Exact Score Tips",
    description:
      "Today's correct score predictions and exact-score betting tips, modelled on expected goals and scoreline patterns for value at high odds.",
    intro:
      "Correct score predictions for today's fixtures, built from expected goals and scoreline-signature patterns to highlight the most likely — and most valuable — exact scores.",
    keywords:
      "correct score prediction today, correct score today, today correct score, 100 sure correct score prediction",
    timeframe: "today",
    marketMatchers: ["correct score", "scoreline"],
    sort: "confidence",
    faqs: [
      {
        question: "Why is correct score betting so hard?",
        answer:
          "There are many possible scorelines, so exact-score bets rarely land — which is why they pay big odds. We narrow the field to the most probable results instead of guessing.",
      },
    ],
  },
  {
    slug: "correct-score-tomorrow",
    label: "Correct Score Tomorrow",
    h1: "Correct Score Predictions Tomorrow",
    title: "Correct Score Predictions Tomorrow | Exact Score Tips",
    description:
      "Tomorrow's correct score predictions and exact-score tips, modelled on expected goals and scoreline patterns for high-odds value.",
    intro:
      "Correct score predictions for tomorrow's fixtures, using expected goals and scoreline patterns to surface the strongest exact-score value.",
    keywords:
      "correct score tomorrow, correct score prediction tomorrow, exact score tips tomorrow",
    timeframe: "tomorrow",
    marketMatchers: ["correct score", "scoreline"],
    sort: "confidence",
    faqs: [
      {
        question: "How should I bet correct score for value?",
        answer:
          "Back two or three of the most probable scorelines rather than a single result, and only when the combined odds still offer value against your estimate.",
      },
    ],
  },
  {
    slug: "btts-today",
    label: "BTTS Today",
    h1: "BTTS Predictions Today",
    title: "BTTS Predictions Today | Both Teams To Score Tips",
    description:
      "Today's Both Teams To Score (BTTS) predictions backed by attacking and defensive form analysis, with value odds and clear reasoning.",
    intro:
      "Both Teams To Score predictions for today's fixtures, combining attacking output and defensive frailty to spot reliable GG and NG opportunities.",
    keywords:
      "btts predictions today, both teams to score today, bts predictions for today, gg predictions today",
    timeframe: "today",
    marketMatchers: ["both teams to score", "btts", "gg"],
    sort: "confidence",
    faqs: [
      {
        question: "What is BTTS in football betting?",
        answer:
          "BTTS (Both Teams To Score) wins if both sides score at least one goal. It's popular because a comeback or late goal can still land the bet.",
      },
    ],
  },
  {
    slug: "over-2-5-today",
    label: "Over 2.5 Today",
    h1: "Over 2.5 Goals Predictions Today",
    title: "Over 2.5 Goals Predictions Today | Goals Betting Tips",
    description:
      "Today's Over 2.5 goals predictions driven by expected goals, attacking form and defensive frailty, with value odds and reasoning.",
    intro:
      "Over 2.5 goals predictions for today's fixtures, using expected-goals modelling and scoring trends to find high-value totals in the most goal-heavy matches.",
    keywords:
      "over 2.5 prediction today, over 2.5 prediction, over 2.5 goals tips today",
    timeframe: "today",
    marketMatchers: ["over 2.5", "over 2,5", "over/under", "over", "totals"],
    sort: "confidence",
    faqs: [
      {
        question: "What does Over 2.5 goals mean?",
        answer:
          "Over 2.5 goals wins if a match has three or more total goals. Two goals or fewer means the bet loses.",
      },
    ],
  },
  {
    slug: "draw-predictions-today",
    label: "Draw Predictions Today",
    h1: "Draw Predictions Today",
    title: "Draw Predictions Today | Football Draw Betting Tips",
    description:
      "Today's draw predictions targeting tight, evenly-matched fixtures with value odds, backed by form and our strategy engine.",
    intro:
      "Draw predictions for today's fixtures, surfacing the evenly-matched, low-scoring games most likely to end level — the hardest market to call, modelled on form and defensive strength.",
    keywords:
      "draw predictions today, football draw tips today, win draw win today",
    timeframe: "today",
    marketMatchers: ["draw", "1x2", "match result"],
    sort: "confidence",
    faqs: [
      {
        question: "Are draws hard to predict?",
        answer:
          "Yes — draws are the toughest football outcome to call, which is why they carry longer odds. We focus on tight, evenly-matched, low-scoring fixtures.",
      },
    ],
  },
  {
    slug: "yesterday",
    label: "Yesterday's Predictions",
    h1: "Yesterday's Football Predictions & Results",
    title: "Yesterday's Football Predictions & Results | Statlign",
    description:
      "Yesterday's football predictions with results — every pick from our engine, transparent win/loss outcomes, no hindsight edits.",
    intro:
      "A full log of yesterday's picks so you can judge the engine on its actual output. Every prediction is shown with its result, no cherry-picking.",
    keywords:
      "predictions yesterday, yesterday football predictions, yesterday soccer prediction results, football results yesterday",
    timeframe: "yesterday",
    sort: "soonest",
    faqs: [
      {
        question: "Where can I see yesterday's results?",
        answer:
          "Every pick our engine published for yesterday's fixtures is listed here with its outcome. We don't hide losing bets — full transparency builds trust.",
      },
      {
        question: "Do you delete losing predictions from history?",
        answer:
          "No. Losing picks stay in the record. That's the only honest way to publish a track record and it's how you can judge whether the engine is worth following.",
      },
    ],
  },
  {
    slug: "tonight",
    label: "Tonight's Football Tips",
    h1: "Tonight's Football Predictions & Betting Tips",
    title: "Tonight's Football Predictions & Betting Tips | Statlign",
    description:
      "Tonight's football predictions and betting tips ranked by confidence, with value odds and clear reasoning for every evening kick-off.",
    intro:
      "Football tips for tonight's fixtures, ranked by confidence with transparent, data-driven reasoning across every major evening league.",
    keywords:
      "football tips tonight, betting tips tonight, best bets tonight, football predictions tonight, soccer tips tonight",
    timeframe: "tonight",
    sort: "confidence",
    faqs: [
      {
        question: "Which fixtures count as 'tonight'?",
        answer:
          "Any match kicking off after 17:00 local through midnight. We rank tonight's slate by our engine's confidence score.",
      },
    ],
  },
  {
    slug: "jackpot",
    label: "Jackpot Predictions",
    h1: "Jackpot Predictions & Accumulator Tips",
    title: "Jackpot Predictions & Multi-Match Tips | Statlign",
    description:
      "Independent AI-driven jackpot predictions and multi-match accumulator tips. Playable on any operator's slip — we're not affiliated with any bookmaker.",
    intro:
      "Statlign publishes independent AI-generated picks for multi-match jackpot slips. We're not affiliated with any bookmaker or operator — use these picks on whichever legal jackpot you play.",
    keywords:
      "jackpot prediction, jackpot tips today, accumulator jackpot picks, multi-match football tips, jackpot football predictions",
    timeframe: "weekend",
    minConfidence: 65,
    sort: "confidence",
    faqs: [
      {
        question: "Are these picks tied to a specific operator's jackpot?",
        answer:
          "No. Statlign is independent. We publish the fixtures and picks our engine backs; you can stake them on any legal jackpot slip that covers those matches.",
      },
      {
        question: "How many matches do jackpot slips usually carry?",
        answer:
          "Operator jackpots typically run 13, 15 or 17 fixtures. We surface high-confidence picks for the weekend's biggest slate so you can build any slip.",
      },
    ],
  },
  {
    slug: "mega-jackpot",
    label: "Mega Jackpot Picks",
    h1: "Mega Jackpot Predictions (17-Match Slates)",
    title: "Mega Jackpot Predictions | Weekend Multi-Match Picks",
    description:
      "Weekend mega jackpot predictions covering large multi-match slates. Independent AI picks — not affiliated with any operator.",
    intro:
      "Our engine's strongest picks for the weekend's biggest multi-match slates, ranked by confidence and value. Statlign is independent — not affiliated with any operator.",
    keywords:
      "mega jackpot prediction, mega jackpot picks, 17 games jackpot predictions, weekend mega accumulator, weekend jackpot tips",
    timeframe: "weekend",
    minConfidence: 65,
    sort: "confidence",
    faqs: [
      {
        question: "Do you cover all 17 fixtures on a weekend mega slate?",
        answer:
          "We publish picks for every fixture our engine has an approved model on. You can build a full slip from the highest-confidence rows.",
      },
    ],
  },
  {
    slug: "midweek-jackpot",
    label: "Midweek Jackpot Picks",
    h1: "Midweek Jackpot Predictions",
    title: "Midweek Jackpot Predictions & Tips | Statlign",
    description:
      "Midweek jackpot predictions and multi-match tips for Tuesday–Thursday fixtures. Independent AI picks — not affiliated with any operator.",
    intro:
      "Midweek multi-match picks generated by our engine. Statlign is independent — not affiliated with any operator or jackpot promotion.",
    keywords:
      "midweek jackpot prediction, midweek jackpot tips, midweek football accumulator, wednesday football predictions",
    timeframe: "today",
    minConfidence: 65,
    sort: "confidence",
    faqs: [
      {
        question: "When are midweek jackpots usually played?",
        answer:
          "Most operator midweek jackpots run Tuesday–Thursday, covering European midweek fixtures. Our engine refreshes picks continuously through the week.",
      },
    ],
  },
  {
    slug: "accumulator-today",
    label: "Accumulator Tips Today",
    h1: "Football Accumulator Tips Today",
    title: "Football Accumulator Tips Today | Acca Builder | Statlign",
    description:
      "Today's football accumulator tips and acca picks, ranked by confidence with value odds. Build a smart multi-leg slip in seconds.",
    intro:
      "Today's highest-confidence picks lined up for an accumulator — pick your legs by confidence, market and value. No pick is guaranteed.",
    keywords:
      "football accumulator tips, acca tips today, daily accumulator tips, football acca today, best accumulator tips",
    timeframe: "today",
    minConfidence: 68,
    sort: "confidence",
    faqs: [
      {
        question: "How many legs should an accumulator have?",
        answer:
          "Fewer legs generally means better hit rate; more legs means bigger payout. Most bettors find 3–6 legs the best balance of value and probability.",
      },
    ],
  },
  {
    slug: "premium-picks-today",
    label: "Premium Picks Today",
    h1: "Premium Football Picks Today",
    title: "Premium Football Picks Today | High-Confidence Tips | Statlign",
    description:
      "Today's premium football picks — our highest-confidence selections with the strongest strategy confirmation. No pick is ever guaranteed.",
    intro:
      "Our top-tier picks for today, backed by the strongest strategy confirmation from the engine. No prediction is guaranteed; treat these as high-conviction, not certainties.",
    keywords:
      "premium football picks today, high confidence football tips, best football tips today, top rated football predictions",
    timeframe: "today",
    minConfidence: 75,
    sort: "confidence",
    faqs: [
      {
        question: "What makes a pick 'premium'?",
        answer:
          "Premium picks are our top-confidence rows for today (75%+ model confidence) with multiple strategy signals confirmed. High conviction, not a guarantee.",
      },
    ],
  },
  {
    slug: "ht-ft-today",
    label: "HT/FT Tips Today",
    h1: "Half-Time / Full-Time Predictions Today",
    title: "HT/FT Predictions Today | Half-Time Full-Time Tips | Statlign",
    description:
      "Today's Half-Time / Full-Time (HT/FT) football predictions, modelled on scoring rhythm and in-game momentum for high-odds value.",
    intro:
      "HT/FT tips for today's fixtures, generated from scoring-timing patterns and in-game momentum signals. High-odds market — used sparingly for value.",
    keywords:
      "ht ft prediction, half time full time tips, ht ft tips today, half time full time predictions",
    timeframe: "today",
    marketMatchers: ["ht/ft", "ht-ft", "half time", "half-time"],
    sort: "confidence",
    faqs: [
      {
        question: "Why does HT/FT pay big odds?",
        answer:
          "HT/FT requires you to predict the score at both half-time and full-time. Because two outcomes must both land, odds are high — but hit rate is lower. We use it selectively for value.",
      },
    ],
  },
  {
    slug: "double-chance-today",
    label: "Double Chance Tips Today",
    h1: "Double Chance Predictions Today",
    title: "Double Chance Predictions Today | 1X, 12, X2 Tips | Statlign",
    description:
      "Today's Double Chance predictions (1X, 12, X2) — covering two of three 1X2 outcomes for higher hit-rate football tips.",
    intro:
      "Double Chance tips for today's fixtures — cover two of three outcomes for a higher hit rate at lower odds. Ideal for underdog spots the engine rates as competitive.",
    keywords:
      "double chance tips, double chance prediction today, 1x tips today, x2 tips today, 12 double chance",
    timeframe: "today",
    marketMatchers: ["double chance", "1x", "x2"],
    sort: "confidence",
    faqs: [
      {
        question: "What does Double Chance cover?",
        answer:
          "1X wins on home or draw. X2 wins on draw or away. 12 wins on either team — losing only on a draw. Lower odds, higher hit rate than a straight 1X2 bet.",
      },
    ],
  },
  {
    slug: "draw-no-bet-today",
    label: "Draw No Bet Tips Today",
    h1: "Draw No Bet Predictions Today",
    title: "Draw No Bet Predictions Today | DNB Tips | Statlign",
    description:
      "Today's Draw No Bet (DNB) predictions — back a side to win with your stake returned on a draw. Lower risk than a straight 1X2 pick.",
    intro:
      "DNB tips for today. Your stake is returned if the match ends level, so you're only exposed to the losing side — a smart hedge on close-priced favourites.",
    keywords:
      "draw no bet tips, dnb prediction today, draw no bet football tips",
    timeframe: "today",
    marketMatchers: ["draw no bet", "dnb"],
    sort: "confidence",
    faqs: [
      {
        question: "How does Draw No Bet work?",
        answer:
          "You back a team to win; if the match is a draw your stake is refunded. It's a way to buy insurance against the draw at slightly reduced odds.",
      },
    ],
  },
  {
    slug: "1x2-today",
    label: "1X2 Tips Today",
    h1: "1X2 Football Predictions Today",
    title: "1X2 Football Predictions Today | Match Result Tips | Statlign",
    description:
      "Today's 1X2 football predictions — home win, draw or away win — ranked by our engine's confidence with clear reasoning.",
    intro:
      "The classic match-result market. Every 1X2 pick our engine approves for today, ranked by confidence with the strategy signals that triggered each call.",
    keywords:
      "1x2 prediction today, 1x2 football tips, match result predictions, 1x2 bet tips today, sure 1x2 tips",
    timeframe: "today",
    marketMatchers: ["1x2", "match result", "match winner"],
    sort: "confidence",
    faqs: [
      {
        question: "Is 1X2 the same as match result?",
        answer:
          "Yes. 1 is home win, X is draw, 2 is away win. It's the most popular football market and where most bettors start.",
      },
    ],
  },
  {
    slug: "mathematical-today",
    label: "Mathematical Predictions Today",
    h1: "Mathematical Football Predictions Today",
    title: "Mathematical Football Predictions Today | Model-Based Tips",
    description:
      "Today's mathematical football predictions — Poisson, expected goals and probability-driven picks with transparent model reasoning.",
    intro:
      "Model-driven picks for today's fixtures, generated from Poisson scoring distributions, expected goals and 50+ statistical strategies. Every pick shows the reasoning.",
    keywords:
      "mathematical football prediction, mathematical predictions today, soccer predictions mathematical, poisson football predictions, xg football predictions",
    timeframe: "today",
    sort: "confidence",
    faqs: [
      {
        question: "What makes a prediction 'mathematical'?",
        answer:
          "Every Statlign pick is model-generated — Poisson goal distributions, expected goals, form indices and 50+ strategy signals combined with a confidence score. No gut calls.",
      },
    ],
  },
  {
    slug: "gg-today",
    label: "GG (Goal-Goal) Tips Today",
    h1: "GG / Goal-Goal Predictions Today",
    title: "GG Predictions Today | Goal-Goal Football Tips | Statlign",
    description:
      "Today's GG (Goal-Goal / Both Teams To Score) predictions — attacking form + defensive frailty combined for high-hit-rate picks.",
    intro:
      "Goal-Goal tips for today's fixtures. Same market as BTTS: both teams must score. Modelled on attacking output and defensive frailty.",
    keywords:
      "gg prediction today, goal goal prediction, gg tips today, gg football predictions",
    timeframe: "today",
    marketMatchers: ["both teams to score", "btts", "gg"],
    sort: "confidence",
    faqs: [
      {
        question: "Is GG the same as BTTS?",
        answer:
          "Yes. GG (Goal-Goal) and BTTS (Both Teams To Score) are the same market — both teams must score at least one goal.",
      },
    ],
  },
  {
    slug: "high-odds-today",
    label: "High Odds Predictions Today",
    h1: "High-Odds Predictions Today",
    title: "High-Odds Football Predictions Today | Value Odds | Statlign",
    description:
      "Today's high-odds football predictions — value-priced picks (7.00+ odds) where our engine sees a statistical edge over the bookmaker.",
    intro:
      "Today's high-value odds picks. These are longer prices where our engine sees a genuine edge — high reward, low hit-rate. Stake small, bet responsibly.",
    keywords:
      "high odds predictions today, big odds tips today, value odds football, high odds football tips, 7 odds daily",
    timeframe: "today",
    sort: "odds_desc",
    faqs: [
      {
        question: "How do you find high-odds value?",
        answer:
          "Our engine estimates a fair probability for every outcome and compares it to the bookmaker's implied probability. When the gap is large enough, we surface the pick. Longer odds mean lower hit rate — stake accordingly.",
      },
    ],
  },
  {
    slug: "nigeria-today",
    label: "Football Predictions Nigeria",
    h1: "Football Predictions Today for Nigerian Fans",
    title: "Football Predictions Today Nigeria | Betting Tips | Statlign",
    description:
      "Today's football predictions for Nigerian fans: EPL, NPFL and European picks with confidence scores, odds to compare on Bet9ja or SportyBet, and prices in Naira.",
    ogTitle: "Football Predictions Today — Nigeria",
    ogDescription: "Today's picks with confidence scores and clear reasoning. Pay in Naira. 10 free welcome credits.",
    intro:
      "Today's football predictions for Nigerian fans, ranked by confidence. Every pick shows the strategies behind it, so you can compare the odds with your bookmaker before you decide. Credits and Premium can be paid in Naira by card, bank transfer or USSD.",
    keywords:
      "football predictions today nigeria, betting tips nigeria, sure odds alternative, bet9ja predictions, sportybet predictions, npfl predictions, epl predictions nigeria, naija football tips",
    timeframe: "today",
    sort: "confidence",
    sections: [
      {
        heading: "Built for Nigerian football fans",
        paragraphs: [
          "Statlign covers the leagues Nigerian fans follow most — the Premier League, LaLiga, Serie A, the Champions League and the NPFL when fixture data is available. Kick-off times show in your local time (West Africa Time).",
          "Our picks focus on the markets Nigerian bookmakers list most: 1X2, Double Chance, Draw No Bet, Over/Under, GG/NG (Both Teams To Score) and correct score. Compare the odds on Bet9ja, SportyBet, BetKing or 1xBet with the confidence score before you stake.",
        ],
      },
      {
        heading: "Pay in Naira",
        paragraphs: [
          "Credit packs and Premium can be paid in Naira through Paystack with card, bank transfer or USSD. New members get 10 free welcome credits to unlock their first picks.",
          "No prediction is guaranteed. Bet only what you can afford to lose. Betting is for adults aged 18 and over.",
        ],
      },
    ],
    faqs: [
      {
        question: "Can I pay for Statlign in Naira?",
        answer:
          "Yes. Credit packs and Premium can be paid in Naira through Paystack using card, bank transfer or USSD.",
      },
      {
        question: "Which bookmakers can I use with these predictions?",
        answer:
          "Statlign is independent. Our picks use standard markets, so you can compare the odds on any licensed Nigerian bookmaker such as Bet9ja, SportyBet or BetKing.",
      },
      {
        question: "Are these sure predictions?",
        answer:
          "No. Nothing in football is certain. Every pick carries a confidence score and the reasoning behind it, and our full win and loss record is public on the Track Record page.",
      },
    ],
  },
  {
    slug: "champions-league",
    label: "Champions League Predictions",
    h1: "Champions League Predictions & UCL Betting Tips",
    title: "Champions League Predictions | UCL Betting Tips | Statlign",
    description:
      "Champions League predictions for every upcoming UCL fixture, plus our outright winner projection — AI match analysis, confidence scores and the strategies behind each pick.",
    ogTitle: "Champions League Predictions",
    ogDescription: "Every UCL fixture modelled: confidence scores, value odds and the outright race.",

    intro:
      "Champions League predictions for the current UCL round, each with a confidence score, the value odds our engine found and the exact strategies that triggered the pick. Below the fixtures you'll find our seasonal outright projection and how to read the numbers.",
    keywords:
      "champions league predictions, who will win the champions league, UCL predictions, champions league betting tips, champions league outright winner odds, ucl match predictions today",
    timeframe: "week",
    leagueMatchers: ["champions league", "uefa champions", "ucl"],
    sort: "confidence",
    sections: [
      {
        heading: "How our Champions League predictions are built",
        paragraphs: [
          "Every Champions League pick starts from the same pipeline that powers the rest of Statlign: fixture and lineup data are ingested, odds are pulled from multiple bookmakers, and a 70+ strategy engine scores each available market. A pick is only published when at least one validated strategy triggers and the underlying match data passes our quality gate.",
          "UCL fixtures need extra care because the sample sizes are small — teams meet in a group or league phase only once or twice, and squad rotation around domestic weekends is heavy. We therefore weight recent domestic form, continental travel distance, rest days between fixtures and confirmed team news more heavily than we do in a domestic-league model.",
        ],
        bullets: [
          "Form divergence: where a side's underlying performance differs from the price the bookmaker has set.",
          "Rotation risk: midweek European fixtures scheduled between two demanding domestic games.",
          "Home continental record: knockout and league-phase home records are modelled separately from domestic home form.",
          "Goal-timing profiles: used for Over/Under, BTTS and half-specific markets rather than 1X2.",
          "Market efficiency filter: UCL prices are among the sharpest in football, so we only publish where the modelled edge survives a stricter threshold.",
        ],
      },
      {
        heading: "Who will win the Champions League? Our outright projection",
        paragraphs: [
          "Our outright projection is a probability distribution, not a single name. We simulate the remaining fixture path for every qualified side thousands of times, using each team's modelled attack and defence strength, the seeding rules for the next round and the venue of the final. The result is a percentage chance of lifting the trophy for each club, refreshed after every completed matchday.",
          "Read it as a market check rather than a tip. If a club's simulated chance is materially higher than the price implied by the bookmaker's outright odds, that is where long-term value sits — but outright bets tie up stake for months and carry a low hit rate by definition. Stake accordingly.",
          "Historically, the eventual winner is almost always a side that combines a top-two domestic position with a squad deep enough to rotate. Teams that arrive in the knockout rounds while fighting a domestic title race and a cup run tend to regress in our model, and that is priced into the projection.",
        ],
      },
      {
        heading: "Which Champions League markets offer the best value",
        paragraphs: [
          "Straight 1X2 prices in the Champions League are efficient, so the edge usually sits in derived markets. Double Chance and Draw No Bet are our most productive UCL markets because they let us back a strong statistical read without paying for the volatility of a one-off European night.",
          "Over/Under and Both Teams To Score are the second cluster we lean on. Group- and league-phase games between mismatched sides produce reliable goal profiles, and our goal-timing model handles the first-half markets that most bookmakers price loosely.",
          "We publish correct-score and half-time/full-time picks only when several independent strategies agree, because the variance in those markets is high enough to erase a real edge over a short season.",
        ],
      },
      {
        heading: "How to use this page",
        paragraphs: [
          "Fixtures are listed most-confident first. Open any card to see the full reasoning: the strategies that triggered, the data quality behind the match and the value gap our engine measured against the bookmaker price. Picks refresh continuously as odds move and team news lands, so it is worth returning closer to kick-off.",
          "No prediction is guaranteed. Confidence scores describe how strongly our models agree, not a certainty of winning. Bet only what you can afford to lose and use the staking guidance in our bankroll guide.",
        ],
      },
    ],
    faqs: [
      {
        question: "Who will win the Champions League this season?",
        answer:
          "Our model publishes a probability for every qualified club rather than a single answer, and refreshes it after each matchday. Sides combining a top-two domestic position with a squad deep enough to rotate consistently rank highest, because our simulations penalise teams carrying heavy domestic and cup workloads into the knockout rounds.",
      },
      {
        question: "How accurate are Champions League predictions?",
        answer:
          "UCL prices are among the sharpest in football, so we apply a stricter edge threshold here than in domestic leagues and publish fewer picks per round. Every published pick carries a confidence score from our strategy engine and a full breakdown of the reasoning — but no prediction is guaranteed.",
      },
      {
        question: "Which Champions League markets do you cover?",
        answer:
          "1X2, Double Chance, Draw No Bet, Over/Under goals (1.5, 2.5, 3.5), Both Teams To Score, half-specific goal markets, correct score and half-time/full-time. Derived markets such as Double Chance and Draw No Bet typically carry our strongest UCL signals.",
      },
      {
        question: "When are Champions League predictions published?",
        answer:
          "Picks for a matchday appear as soon as opening odds and provisional team news are available, usually several days before kick-off, and are updated continuously as prices move and lineups are confirmed.",
      },
      {
        question: "Do you cover the Champions League knockout rounds?",
        answer:
          "Yes. Knockout ties are modelled separately from the league phase because two-legged aggregate scenarios change how teams approach each match, and our home/away continental records are tracked independently of domestic form.",
      },
    ],
  },
];


export function findLanding(slug: string): PredictionLanding | undefined {
  return PREDICTION_LANDINGS.find((l) => l.slug === slug);
}

/**
 * Retired slugs that should 301-style redirect to their current equivalent.
 * "sure-wins-today" was renamed to "bonus-picks-today" to avoid implying a
 * guaranteed/100%-win outcome (misleading and non-compliant in most markets).
 */
export const LANDING_ALIASES: Record<string, string> = {
  "sure-wins-today": "bonus-picks-today",
};

export const LANDING_ROUTES = PREDICTION_LANDINGS.map((l) => `/predictions/${l.slug}`);

/** UTC kickoff-time bounds (ISO strings) for a timeframe. */
export function landingWindow(timeframe: Timeframe): { from: string; to: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  if (timeframe === "today") {
    return {
      from: new Date(Date.UTC(y, m, d, 0, 0, 0)).toISOString(),
      to: new Date(Date.UTC(y, m, d, 23, 59, 59)).toISOString(),
    };
  }
  if (timeframe === "tomorrow") {
    return {
      from: new Date(Date.UTC(y, m, d + 1, 0, 0, 0)).toISOString(),
      to: new Date(Date.UTC(y, m, d + 1, 23, 59, 59)).toISOString(),
    };
  }
  if (timeframe === "yesterday") {
    return {
      from: new Date(Date.UTC(y, m, d - 1, 0, 0, 0)).toISOString(),
      to: new Date(Date.UTC(y, m, d - 1, 23, 59, 59)).toISOString(),
    };
  }
  if (timeframe === "tonight") {
    // 17:00 UTC today → 03:00 UTC tomorrow (covers most evening kick-offs globally).
    return {
      from: new Date(Date.UTC(y, m, d, 17, 0, 0)).toISOString(),
      to: new Date(Date.UTC(y, m, d + 1, 3, 0, 0)).toISOString(),
    };
  }
  if (timeframe === "week") {
    // Rolling 7-day window — used by competition hubs (e.g. UCL) whose fixtures
    // fall on midweek dates rather than a single day or weekend.
    return {
      from: new Date(Date.UTC(y, m, d, 0, 0, 0)).toISOString(),
      to: new Date(Date.UTC(y, m, d + 7, 23, 59, 59)).toISOString(),
    };
  }

  // weekend: next Saturday 00:00 → Sunday 23:59 (or current weekend if it's Sat/Sun).
  const dow = now.getUTCDay(); // 0 Sun … 6 Sat
  const daysUntilSat = dow === 0 ? -1 : 6 - dow; // Sunday counts as in-weekend
  const satOffset = dow === 0 ? -1 : daysUntilSat;
  const sat = new Date(Date.UTC(y, m, d + satOffset, 0, 0, 0));
  const sun = new Date(Date.UTC(y, m, d + satOffset + 1, 23, 59, 59));
  return { from: sat.toISOString(), to: sun.toISOString() };
}
